import { FC, Suspense, lazy, useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary';
import AuctionBiddingModalSpinner from './components/AuctionBiddingModalSpinner';
import { STEPS } from './AuctionBiddingModalContext';
import type { Currency, OnBidPlaced } from './types';

import styles from './styles/AuctionBiddingModal.scss';

const AuctionBiddingModalRenderer = lazy(
    () =>
        import(
            /* webpackChunkName: "AuctionBiddingModalRenderer" */ './AuctionBiddingModalRenderer'
        )
);

type Props = {
    isBiddingDisabled?: boolean;
    isMobile?: boolean;
    onClose: () => void;
    onBidPlaced: OnBidPlaced;
    currency: string;
    itemId: string;
    auctionLotId: string;
};

const AuctionBiddingModal: FC<Props> = props => {
    const { isBiddingDisabled, isMobile, onClose, currency, itemId, onBidPlaced, auctionLotId } =
        props;

    // force relay hook to re-evaluate query on mount
    const fetchKeyRef = useRef<number | null>(null);

    const getFetchKey = (): number => {
        if (fetchKeyRef.current === null) {
            fetchKeyRef.current = +new Date();
        }
        return fetchKeyRef.current;
    };

    const [modalSize, setModalSize] = useState<'medium' | undefined>();

    const onStepChange = useCallback((step: keyof typeof STEPS) => {
        if (step === STEPS.SHIPPING_INFO) {
            setModalSize('medium');
        } else {
            setModalSize(undefined);
        }
    }, []);

    return (
        <ModalContainer isOpen onClose={onClose} modalSize={modalSize}>
            <ModalCloseButton className={styles.closeButton} onClick={onClose} />
            <ModalBody className={styles.modalBody}>
                <ErrorBoundary
                    fallback={() => (
                        <div>
                            <FormattedMessage
                                id="auctionBiddingModal.loadingError"
                                defaultMessage="There was an error loading. Please try again."
                            />
                        </div>
                    )}
                >
                    <Suspense fallback={<AuctionBiddingModalSpinner />}>
                        <AuctionBiddingModalRenderer
                            currency={currency as Currency}
                            itemId={itemId}
                            fetchKey={getFetchKey()}
                            isMobile={isMobile}
                            isBiddingDisabled={isBiddingDisabled}
                            onStepChange={onStepChange}
                            onBidPlaced={onBidPlaced}
                            auctionLotId={auctionLotId}
                        />
                    </Suspense>
                </ErrorBoundary>
            </ModalBody>
        </ModalContainer>
    );
};

export default AuctionBiddingModal;
